export const MAX_COMMENT_REPLY_DEPTH = 4;

export interface ThreadComment {
  id: string;
  parent_id?: string | null;
  replies?: ThreadComment[];
  deleted_at?: string | null;
  deleted_by?: string | null;
  text?: string | null;
}

export function nestedReplies<T extends { id?: string }>(comment: unknown): T[] {
  const replies = (comment as { replies?: T[] } | null | undefined)?.replies;
  return (replies || []).filter((reply): reply is T => !!reply && typeof reply.id === 'string');
}

export function flattenComments<T extends { id: string }>(
  comments: T[],
  acc: Record<string, T> = {},
): Record<string, T> {
  comments.forEach((comment) => {
    if (!comment?.id) return;
    acc[comment.id] = comment;
    flattenComments(nestedReplies<T>(comment), acc);
  });
  return acc;
}

export function mergeComment<T extends { id?: string }>(existing: T | undefined, incoming: T): T {
  const incomingReplies = nestedReplies<T>(incoming);
  const existingReplies = nestedReplies<T>(existing);
  const replies = incomingReplies.length ? incomingReplies : existingReplies;
  const merged = existing ? { ...existing, ...incoming } : { ...incoming };
  if (!replies.length) {
    delete (merged as { replies?: T[] }).replies;
    return merged;
  }
  return { ...merged, replies } as T;
}

export function replaceInTree<T extends { id: string }>(
  comment: T,
  commentId: string,
  nextComment: T,
): T {
  if (!comment) return comment;
  if (comment.id === commentId) return nextComment;
  const replies = nestedReplies<T>(comment);
  if (!replies.length) return comment;

  let changed = false;
  const nextReplies = replies.map((reply) => {
    const next = replaceInTree(reply, commentId, nextComment);
    if (next !== reply) changed = true;
    return next;
  });
  return changed ? { ...comment, replies: nextReplies } as T : comment;
}

export function updateStoredComment<T extends { id: string }>(
  items: Record<string, T>,
  commentId: string,
  updater: (existing: T) => T,
): Record<string, T> {
  const existing = items[commentId];
  if (!existing) return items;

  const nextComment = updater(existing);
  const withItem = { ...items, [commentId]: nextComment };
  return Object.keys(withItem).reduce((acc, id) => {
    acc[id] = replaceInTree(withItem[id], commentId, nextComment);
    return acc;
  }, {} as Record<string, T>);
}

function insertInto<T extends { id: string; parent_id?: string | null }>(
  item: T,
  comment: T,
): T {
  if (!item) return item;
  if (item.id === comment.parent_id) {
    const replies = nestedReplies<T>(item);
    if (replies.some((reply) => reply.id === comment.id)) return item;
    return { ...item, replies: [...replies, comment] } as T;
  }

  const replies = nestedReplies<T>(item);
  if (!replies.length) return item;

  let changed = false;
  const nextReplies = replies.map((reply) => {
    const next = insertInto(reply, comment);
    if (next !== reply) changed = true;
    return next;
  });
  return changed ? { ...item, replies: nextReplies } as T : item;
}

export function storePostedComment<T extends { id: string; parent_id?: string | null }>(
  items: Record<string, T>,
  incoming: T,
): Record<string, T> {
  const comment = mergeComment(items[incoming.id], incoming);
  const withItem = { ...items, [comment.id]: comment };
  if (!comment.parent_id) return withItem;

  return Object.keys(withItem).reduce((acc, id) => {
    acc[id] = insertInto(withItem[id], comment);
    return acc;
  }, {} as Record<string, T>);
}

export function insertReply<T extends { id: string; parent_id?: string | null }>(
  comments: T[],
  comment: T,
): T[] {
  if (!comment.parent_id) return [...comments, comment];
  return comments.map((item) => insertInto(item, comment));
}

export function markCommentDeleted<T extends {
  id: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}>(
  comments: T[],
  commentId: string,
  deletedAt: string,
  deletedBy?: string,
): T[] {
  return comments.map((comment) => {
    if (comment.id === commentId) {
      return {
        ...comment,
        text: null,
        deleted_at: deletedAt,
        deleted_by: deletedBy ?? comment.deleted_by,
      };
    }
    const replies = nestedReplies<T>(comment);
    if (!replies.length) return comment;
    return {
      ...comment,
      replies: markCommentDeleted(replies, commentId, deletedAt, deletedBy),
    } as T;
  });
}

type CountableComment = { deleted_at?: string | null; replies?: unknown };

export function countVisibleComments(
  comments: CountableComment[] | null | undefined,
): number {
  return (comments || []).reduce((sum, comment) => {
    const self = comment.deleted_at ? 0 : 1;
    const replies = Array.isArray(comment.replies) ? comment.replies as CountableComment[] : [];
    return sum + self + countVisibleComments(replies);
  }, 0);
}

export function mapCommentTree<T extends { id?: string }>(
  comments: T[],
  mapComment: (comment: T) => T,
): T[] {
  return (comments || []).map((comment) => {
    const next = mapComment(comment);
    const replies = nestedReplies<T>(next);
    if (!replies.length) return next;
    return { ...next, replies: mapCommentTree(replies, mapComment) } as T;
  });
}

export interface ReplyTargetInput {
  commentId: string;
  parentId?: string | null;
  depth: number;
  name: string;
  mentionUserId?: string | null;
  isNote: boolean;
}

export interface ReplyTarget {
  parentId: string;
  replyToCommentId?: string;
  name: string;
  mentionUserId?: string;
  isNote: boolean;
}

export function buildReplyTarget(input: ReplyTargetInput): ReplyTarget | null {
  const isMaxDepth = input.depth >= MAX_COMMENT_REPLY_DEPTH;
  const parentId = isMaxDepth ? input.parentId : input.commentId;
  if (!parentId) return null;

  return {
    parentId,
    replyToCommentId: isMaxDepth ? input.commentId : undefined,
    name: input.name,
    mentionUserId: input.mentionUserId || undefined,
    isNote: input.isNote,
  };
}

export function mentionToken(userId?: string | null) {
  return userId ? `@(${userId}) ` : '';
}

export function applyReplyMention(text: string, previousToken: string, nextToken: string) {
  const withoutPrevious = previousToken && text.startsWith(previousToken)
    ? text.slice(previousToken.length)
    : text;
  if (!nextToken) return withoutPrevious;
  if (withoutPrevious.startsWith(nextToken)) return withoutPrevious;
  return `${nextToken}${withoutPrevious}`;
}
